import * as React from "react";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { seesCampaigns } from "@/lib/types";
import { useAuth } from "@/lib/auth";

/**
 * Setting the firm a `Vary by firm` split reads, from the canvas (ACL-340).
 *
 * A firm or campaign split is not a question: the engine reads the firm off the lead's own tags
 * (`attributes.firm_tag`), so a lead with none matches no branch and takes the Fallback. A rep on
 * a call who has just been told which firm the client is with needs to be able to say so without
 * leaving the board — that is what this is.
 *
 * **It writes the contact, and says that it does.** The same `PATCH /leads/{id}` the contact
 * header uses, so `set_firm` records a system note naming who changed it and the change is
 * attributed rather than quiet. It is not an answer to a question: nothing is stored on the
 * intake, and the route is re-walked from the lead's new firm.
 *
 * **Only the firms the split actually names are offered**, plus what the lead has now. A branch
 * exists per firm the admin chose; offering the tenant's whole list would invite setting a firm
 * that sends the lead straight back to "everyone else".
 */
export function ScopePicker({
  kind,
  options,
  current,
  maySet,
  pending,
  problem,
  onPick,
}: {
  kind: "firm" | "campaign";
  /** The firms or campaigns this split branches on, as the graph names them. */
  options: { id: string; label: string }[];
  /** What the lead has now, when it is one of these. */
  current: string | null;
  /** Whether this reader may record the contact's firm. A rep may (`CONTACT_REP_FIELDS`), which
   *  is the point: they are the one being told it. False while signed in as somebody else, whose
   *  record is not theirs to write (ADR-040), and the server refuses that too. */
  maySet: boolean;
  pending: boolean;
  problem: string | null;
  onPick: (id: string) => void;
}) {
  const sees = seesCampaigns(useAuth().user?.role);
  const [chosen, setChosen] = React.useState<string>(current ?? "");
  React.useEffect(() => setChosen(current ?? ""), [current]);

  // A campaign is the call's, not something to retag a contact with: it is set by which line the
  // lead is called on, so there is nothing here a rep could correctly change.
  if (kind === "campaign") {
    if (!sees) return null;
    return (
      <p className="mt-1 text-2xs text-text-muted">
        Paths here go by campaign, which is the line this lead is called on — not something to set
        by hand.
      </p>
    );
  }

  if (options.length === 0) return null;

  // Signed in as somebody else: their record is not ours to write, and the route refuses it too.
  if (!maySet) {
    return (
      <p className="mt-1 text-2xs text-text-muted">
        {current
          ? "Paths here go by firm. Changing it is not yours to do while signed in as somebody else."
          : "Paths here go by firm, and this lead has none recorded."}
      </p>
    );
  }

  return (
    <div className="mt-2 space-y-1.5">
      <p className="text-2xs text-text-muted">
        {current
          ? "Paths here go by firm. Changing it changes the contact, and the route follows the new one."
          : "Paths here go by firm. Setting it changes the contact, and the route follows."}
      </p>
      <Select value={chosen} onValueChange={setChosen} disabled={pending}>
        <SelectTrigger aria-label="The firm this client is with" className="h-8 text-xs">
          <SelectValue placeholder="Pick the firm" />
        </SelectTrigger>
        <SelectContent>
          {options.map((one) => (
            <SelectItem key={one.id} value={one.id}>
              {one.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {problem ? <p className="text-2xs text-danger-11">{problem}</p> : null}
      <Button
        size="sm"
        className={cn("h-7", !chosen && "opacity-60")}
        disabled={pending || !chosen || chosen === current}
        onClick={() => chosen && onPick(chosen)}
      >
        {current ? "Change the firm" : "Set the firm"}
      </Button>
    </div>
  );
}

/**
 * The firms or campaigns a split branches on, read off its own edges.
 *
 * Only `eq` and `in` clauses name something pickable; an `ne` branch ("every firm but this one")
 * is a rule about everyone else and offering its firm would be backwards.
 */
export function scopeChoices(
  edges: { from_key: string; position: number; label: string | null; conditions: { source: string; op: string; value?: unknown }[] }[],
  key: string,
): { kind: "firm" | "campaign"; options: { id: string; label: string }[] } | null {
  const mine = edges.filter((one) => one.from_key === key);
  const options: { id: string; label: string }[] = [];
  let kind: "firm" | "campaign" | null = null;

  for (const edge of mine) {
    for (const clause of edge.conditions) {
      if (clause.source !== "firm" && clause.source !== "campaign") continue;
      kind ??= clause.source;
      if (clause.source !== kind) continue;
      if (clause.op !== "eq" && clause.op !== "in") continue;
      const values = Array.isArray(clause.value) ? clause.value : [clause.value];
      for (const one of values) {
        if (one === null || one === undefined) continue;
        const id = String(one);
        if (options.some((o) => o.id === id)) continue;
        // The branch's own label is what the admin wrote for it, which reads better on a call
        // than a firm's formal name; a branch naming several falls back to the id.
        options.push({ id, label: values.length === 1 ? (edge.label ?? id) : id });
      }
    }
  }
  return kind ? { kind, options } : null;
}
